import { useState, useEffect, useCallback } from 'react'
import {
  LayoutGrid, RefreshCw, Plus, ExternalLink, AlertTriangle,
  CheckCircle2, XCircle, Clock, Settings, Sparkles,
  Shield, Users, DollarSign, Package, Cloud, Activity, HelpCircle,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useNavigate, Link } from 'react-router-dom'
import { api } from '../lib/api'
import CyberSnapshot from '../components/CyberSnapshot'

interface CustomKpi { label: string; value: string | number; unit?: string; severity?: string }
interface Drilldown { label: string; url: string }

interface ConnectorSummary {
  health: string
  assets_total?: number | null
  assets_coverage_pct?: number | null
  alerts_open?: number | null
  alerts_critical?: number | null
  alerts_24h?: number | null
  incidents_open?: number | null
  incidents_24h?: number | null
  mttr_minutes?: number | null
  licences_used?: number | null
  licences_total?: number | null
  licences_expiring_30d?: number | null
  cost_mtd?: number | null
  cost_currency?: string
  cost_forecast?: number | null
  compliance_pct?: number | null
  custom_kpis?: CustomKpi[]
  headline?: string
  drilldowns?: Drilldown[]
}

interface Tile {
  id: number
  kind: string
  name: string
  vendor: string
  category: string
  icon: string
  last_run_at: string | null
  last_status: string
  last_error: string
  summary: ConnectorSummary
}

interface ExecSummary {
  count: number
  health_counts: Record<string, number>
  rollup: {
    assets_total: number
    alerts_open: number
    alerts_critical: number
    incidents_open: number
    licences_used: number
    licences_total: number
    cost_mtd: number
  }
  stale_connectors: number
  by_category: Record<string, Tile[]>
  tiles: Tile[]
  generated_at: string
  cdm?: {
    asset_count: number
    user_count: number
    person_count: number
    software_install_count: number
    conflict_open_count: number
    orphan_unclaimed_count: number
    stale_adapter_count: number
    ghost_asset_count: number
    last_pull_at: string | null
    cdm_health: string
  } | null
}

const CAT_LABEL: Record<string, string> = {
  security: 'Security',
  endpoint: 'Endpoint',
  itsm: 'ITSM',
  cloud: 'Cloud / Infrastructure',
  observability: 'Observability',
  productivity: 'Productivity',
  cost: 'Finance / Cost',
  other: 'Other',
}

const CAT_COLOR: Record<string, string> = {
  security: 'text-rose',
  endpoint: 'text-amber-light',
  itsm: 'text-cyan',
  cloud: 'text-blue',
  observability: 'text-violet',
  productivity: 'text-emerald',
  cost: 'text-orange',
  other: 'text-text-muted',
}

function HealthDot({ health }: { health?: string }) {
  const color = health === 'healthy' ? 'bg-emerald'
    : health === 'degraded' ? 'bg-amber'
    : health === 'outage' ? 'bg-rose'
    : 'bg-text-muted'
  return (
    <span className={clsx('w-2 h-2 rounded-full shrink-0', color, health === 'outage' && 'animate-pulse')} />
  )
}

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—'
  if (Math.abs(n) >= 1000) return `${(n / 1000).toFixed(1)}k`
  return n.toString()
}

function fmtPct(n: number | null | undefined): string {
  if (n == null) return '—'
  return `${n.toFixed(1)}%`
}

function fmtMoney(n: number | null | undefined, cur = 'USD'): string {
  if (n == null) return '—'
  const sym = cur === 'GBP' ? '£' : cur === 'EUR' ? '€' : '$'
  if (n >= 1000) return `${sym}${(n / 1000).toFixed(1)}k`
  return `${sym}${n.toFixed(0)}`
}

function formatTimeAgo(iso: string | null): string {
  if (!iso) return 'never'
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60) return `${Math.floor(diff)}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

// ── Tile ─────────────────────────────────────────────────────────────────────
function ConnectorTile({ tile, onEdit }: { tile: Tile; onEdit: (id: number) => void }) {
  const s = tile.summary || {}
  const hasError = tile.last_status === 'error'

  return (
    <div
      onClick={() => onEdit(tile.id)}
      className={clsx(
        'card p-3 flex flex-col gap-2 border transition-all cursor-pointer relative group',
        'hover:border-blue/40 hover:shadow-lg',
        hasError ? 'border-rose/30 bg-rose/5' : 'border-border',
      )}
      title="Click to edit this connector"
    >
      {/* Dedicated Edit button in the top-right corner — visible on hover */}
      <button
        type="button"
        onClick={e => { e.stopPropagation(); onEdit(tile.id) }}
        className="absolute top-2 right-2 z-10 px-2 py-0.5 rounded bg-blue text-white text-[9px] font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
        aria-label={`Edit ${tile.name} connector`}
      >
        ✎ Edit
      </button>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <HealthDot health={s.health} />
          <div className="min-w-0">
            <div className="text-[12px] font-bold text-text-primary truncate">{tile.name}</div>
            <div className="text-[9px] text-text-muted uppercase tracking-wider">{tile.vendor}</div>
          </div>
        </div>
        <div className="text-[9px] text-text-muted text-right shrink-0">
          <Clock size={9} className="inline mr-0.5" />
          {formatTimeAgo(tile.last_run_at)}
        </div>
      </div>

      {hasError ? (
        <div className="flex items-start gap-1.5 text-[10px] text-rose bg-rose/5 border border-rose/20 rounded p-2">
          <AlertTriangle size={11} className="shrink-0 mt-0.5" />
          <span className="truncate" title={tile.last_error}>{tile.last_error || 'Fetch failed'}</span>
        </div>
      ) : (
        <>
          {s.headline && (
            <p className="text-[10px] text-text-secondary leading-snug line-clamp-2">{s.headline}</p>
          )}

          {/* Primary KPIs — only show the ones populated */}
          <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px]">
            {s.assets_total != null && (
              <Kpi label="Assets" value={fmtNum(s.assets_total)} icon={Package} />
            )}
            {s.alerts_open != null && (
              <Kpi label="Alerts open" value={fmtNum(s.alerts_open)} icon={AlertTriangle}
                tone={s.alerts_critical && s.alerts_critical > 0 ? 'rose' : undefined} />
            )}
            {s.alerts_critical != null && s.alerts_critical > 0 && (
              <Kpi label="Critical" value={fmtNum(s.alerts_critical)} icon={Shield} tone="rose" />
            )}
            {s.incidents_open != null && s.incidents_open > 0 && (
              <Kpi label="Incidents" value={fmtNum(s.incidents_open)} icon={Activity} tone="amber" />
            )}
            {s.mttr_minutes != null && (
              <Kpi label="MTTR" value={`${Math.round(s.mttr_minutes)}m`} icon={Clock} />
            )}
            {(s.licences_used != null && s.licences_total != null) && (
              <Kpi label="Licences" value={`${fmtNum(s.licences_used)}/${fmtNum(s.licences_total)}`} icon={Users} />
            )}
            {s.cost_mtd != null && (
              <Kpi label="Spend MTD" value={fmtMoney(s.cost_mtd, s.cost_currency)} icon={DollarSign} />
            )}
            {s.compliance_pct != null && (
              <Kpi label="Compliance" value={fmtPct(s.compliance_pct)} icon={CheckCircle2}
                tone={s.compliance_pct < 90 ? 'rose' : s.compliance_pct < 95 ? 'amber' : 'emerald'} />
            )}
            {s.assets_coverage_pct != null && (
              <Kpi label="Coverage" value={fmtPct(s.assets_coverage_pct)} icon={Shield} />
            )}
          </div>

          {/* Custom KPIs */}
          {s.custom_kpis && s.custom_kpis.length > 0 && (
            <div className="border-t border-border/50 pt-1.5 space-y-0.5">
              {s.custom_kpis.slice(0, 5).map((k, i) => (
                <div key={i} className="flex items-center justify-between text-[10px]">
                  <span className="text-text-muted truncate">{k.label}</span>
                  <span className={clsx(
                    'font-mono font-semibold shrink-0',
                    k.severity === 'critical' ? 'text-rose'
                      : k.severity === 'warn' ? 'text-amber-light'
                      : 'text-text-primary',
                  )}>
                    {String(k.value)}{k.unit ? ` ${k.unit}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Drilldowns */}
          {s.drilldowns && s.drilldowns.length > 0 && (
            <div className="flex flex-wrap gap-2 border-t border-border/50 pt-1.5">
              {s.drilldowns.slice(0, 2).map((d, i) => (
                <a
                  key={i}
                  href={d.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  onClick={e => e.stopPropagation()}
                  className="text-[10px] text-blue-light hover:underline inline-flex items-center gap-1"
                >
                  {d.label} <ExternalLink size={9} />
                </a>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function Kpi({ label, value, icon: Icon, tone }: { label: string; value: string; icon: React.ElementType; tone?: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={10} className="text-text-muted shrink-0" />
      <span className="text-text-muted">{label}</span>
      <span className={clsx(
        'ml-auto font-mono font-semibold',
        tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-text-primary',
      )}>
        {value}
      </span>
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function ExecutiveSummaryPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<ExecSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/executive-summary')
      setData(data)
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    fetchData()
    const i = setInterval(fetchData, 60_000)
    return () => clearInterval(i)
  }, [fetchData])

  const refreshAll = async () => {
    setRefreshing(true)
    try {
      await api.post('/api/connectors/refresh-all')
      await fetchData()
    } finally { setRefreshing(false) }
  }

  const openEdit = (id: number) => {
    // Navigate to connectors-manage with ?edit=<id> — the manage page auto-opens the edit form
    navigate(`/app/connectors-manage?edit=${id}`)
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw size={20} className="animate-spin text-text-muted" />
      </div>
    )
  }
  if (!data) return null

  const isEmpty = data.count === 0

  return (
    <div className="space-y-5 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <LayoutGrid size={20} className="text-blue" /> Executive Summary
          </h1>
          <p className="text-[10px] text-text-secondary">
            Phone-friendly cyber snapshot up top, full connector grid below. Pulled every 15 min · normalised KPIs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate('/app/connectors-manage')} className="btn-secondary text-xs">
            <Settings size={12} /> Manage connectors
          </button>
          <button type="button" onClick={refreshAll} disabled={refreshing} className="btn-primary text-xs">
            <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refresh all
          </button>
        </div>
      </div>

      {/* Cyber snapshot — phone-first, threat level + KEV/CVE/ransomware/exposure tiles */}
      <CyberSnapshot />

      {isEmpty ? (
        <div className="card p-10 text-center">
          <LayoutGrid size={36} className="text-text-muted mx-auto mb-3" />
          <div className="text-sm font-semibold text-text-primary mb-1">No connectors configured yet</div>
          <p className="text-[11px] text-text-secondary mb-4 max-w-md mx-auto">
            Add a connector to pull summary KPIs from a commercial tool — CrowdStrike, BMC Helix, SCCM,
            Defender, Azure, AWS, Datadog, Jira, anything with a JSON API. Start with the Demo kind to see
            the layout, or pick HTTP-JSON for any ad-hoc endpoint.
          </p>
          <button type="button" onClick={() => navigate('/app/connectors-manage')} className="btn-primary text-xs">
            <Plus size={12} /> Add first connector
          </button>
        </div>
      ) : (
        <>
          {/* Roll-up strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
            <RollTile icon={LayoutGrid} label="Connectors" value={data.count.toString()} tone="blue" sub={`${data.stale_connectors} stale`} />
            <RollTile icon={CheckCircle2} label="Healthy" value={data.health_counts.healthy?.toString() ?? '0'} tone="emerald" />
            <RollTile icon={AlertTriangle} label="Degraded" value={data.health_counts.degraded?.toString() ?? '0'} tone="amber" />
            <RollTile icon={XCircle} label="Outage" value={data.health_counts.outage?.toString() ?? '0'} tone="rose" />
            <RollTile icon={Shield} label="Open alerts" value={fmtNum(data.rollup.alerts_open)} tone="orange"
              sub={data.rollup.alerts_critical > 0 ? `${data.rollup.alerts_critical} critical` : 'none critical'} />
            <RollTile icon={Users} label="Licences" value={`${fmtNum(data.rollup.licences_used)}/${fmtNum(data.rollup.licences_total)}`} tone="emerald" />
            <RollTile icon={DollarSign} label="Spend MTD" value={fmtMoney(data.rollup.cost_mtd)} tone="blue" />
          </div>

          {/* CDM rollup strip — asset graph + identity + conflicts.
              Surfaces only when CDM bootstrap has run (always true on this
              instance now that Phase 1 is deployed). */}
          {data.cdm && (
            <div className="card p-3 flex items-center justify-between gap-3 flex-wrap"
                 title="Common Data Model — asset graph + identity resolution + write-back">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <Link to="/app/cdm" className="text-emerald font-semibold inline-flex items-center gap-1.5 hover:underline">
                  CDM
                </Link>
                <span className="text-text-muted">·</span>
                <CdmStat label="assets"       value={data.cdm.asset_count} tone="text-emerald" />
                <CdmStat label="users"         value={data.cdm.user_count}  tone="text-blue-light" />
                <CdmStat label="persons"        value={data.cdm.person_count} tone="text-purple-light" />
                <CdmStat label="installs"       value={data.cdm.software_install_count} tone="text-cyan" />
                <CdmStat label="conflicts"     value={data.cdm.conflict_open_count} tone="text-amber-light" warn />
                <CdmStat label="orphans"        value={data.cdm.orphan_unclaimed_count} tone="text-purple-light" warn />
                <CdmStat label="stale adapters" value={data.cdm.stale_adapter_count} tone="text-rose-light" warn />
              </div>
              <div className="text-[10px] text-text-muted flex items-center gap-2">
                <span>last pull: {data.cdm.last_pull_at ? new Date(data.cdm.last_pull_at).toLocaleString() : 'never'}</span>
                <Link to="/app/cdm" className="text-blue-light hover:underline">→ open CDM</Link>
              </div>
            </div>
          )}

          {/* Tiles grouped by category */}
          {Object.entries(data.by_category).map(([cat, tiles]) => (
            <div key={cat} className="space-y-2">
              <h2 className={clsx('text-sm font-semibold flex items-center gap-2', CAT_COLOR[cat] || 'text-text-primary')}>
                <span className="w-1.5 h-1.5 rounded-full bg-current" />
                {CAT_LABEL[cat] || cat}
                <span className="text-[10px] text-text-muted font-normal">{tiles.length}</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {tiles.map(t => <ConnectorTile key={t.id} tile={t} onEdit={openEdit} />)}
              </div>
            </div>
          ))}
        </>
      )}

      <div className="text-[10px] text-text-muted flex items-center gap-1">
        <HelpCircle size={10} />
        Generated {data.generated_at.slice(11, 19)} UTC · auto-refresh every minute. Backend refresh runs every 15 min.
      </div>
    </div>
  )
}

function CdmStat({ label, value, tone, warn }: { label: string; value: number; tone: string; warn?: boolean }) {
  return (
    <span className={clsx(
      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono',
      warn && value > 0 ? 'bg-amber/10 ring-1 ring-amber/30' : '',
    )}>
      <span className={clsx('font-bold', tone)}>{value.toLocaleString()}</span>
      <span className="text-text-muted text-[10px]">{label}</span>
    </span>
  )
}


function RollTile({ icon: Icon, label, value, tone, sub }: {
  icon: React.ElementType; label: string; value: string; tone: string; sub?: string
}) {
  const bg = {
    emerald: 'bg-emerald/10 border-emerald/30 text-emerald',
    amber:   'bg-amber/10 border-amber/30 text-amber-light',
    rose:    'bg-rose/10 border-rose/30 text-rose',
    blue:    'bg-blue/10 border-blue/30 text-blue-light',
    orange:  'bg-orange/10 border-orange/30 text-orange',
  }[tone] || 'bg-surface-card border-border'
  return (
    <div className={clsx('card p-2.5 border', bg)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon size={11} />
          <span className="text-[10px] uppercase tracking-wider font-semibold">{label}</span>
        </div>
        <span className="text-base font-bold font-mono">{value}</span>
      </div>
      {sub && <div className="text-[9px] text-text-muted mt-0.5">{sub}</div>}
    </div>
  )
}
